/**
 * Effective-access resolver (S0.4) — the CORE pure function both consumers derive
 * from: the MSW governance handlers stamp its output into `permissions[]` +
 * `visibleOrgIds[]` at `/auth/me` (S1.1, exactly what a real backend's token
 * claims would do), and the governance preview / explainer run it client-side
 * live over the editable catalogs (S3.1/S3.2). Zero-I/O, no globals, no
 * `Date.now`/`Math.random`, no React, no MSW — the caller supplies the full
 * dataset — so the two consumers are guaranteed identical by the shared-selector
 * parity test (S0.4). Same discipline as `resolvePrice` / `computeReorderSuggestion`.
 *
 * It composes the two orthogonal closures (plan §0), never conflating them:
 *   Axis A (WHAT) — `resolveAxisAGrants`: union of grants over the reflexive-
 *     transitive `inheritsFrom` closure (`resolveRoleClosure`), then expanded by
 *     the S0.2 implication table (`products.manage ⇒ products.view`), every grant
 *     source-tagged (`direct` / `inherited-junior` / `implication`).
 *   Axis B (WHERE) — `resolveVisibleOrgIds` (S0.3): the org subtree the principal
 *     may see, gated by whether Axis A granted `crossFranchiseView`.
 * Access passes iff (A) the grant is held AND (B) the row is in scope. Scope only
 * narrows, never grants.
 *
 * Superadmin is a ROLE that HOLDS all grants, not a code bypass: `role-admin`
 * seeds `[...ALL_PERMISSIONS]`, so `resolveEffectiveAccess` produces the full
 * grant set for it purely by unioning its `permissions` — `isSystem` is
 * deliberately NEVER read here (the "no bypass" invariant, S0.4 test).
 *
 * Degenerate path: with no `inheritsFrom` and no `assignments`, Axis A reduces to
 * today's flat union (plus designed-in phantom `*.view` implications that nothing
 * gates) and Axis B is empty — so Waves 0–1 land with zero user-visible change
 * until the flag flips.
 */

import type {
  AccessDecision,
  DenyRule,
  EffectiveAccess,
  GrantSource,
  PrincipalIdentity,
} from './types';
import { resolveRoleClosure, type RoleClosureNode } from './roleClosure';
import {
  isRecordVisible,
  resolveVisibleOrgIds,
  type OrgScopeNode,
} from './orgScope';
import { directImplications } from '@/constants/permissionImplication';

/**
 * The `crossFranchiseView` structural grant (plan §0 · Q7) — recognised BY THE
 * RESOLVER to let Axis-B scope descend past a `model==='franchise'` descent-stop.
 * This literal is the cross-slice contract: the seed
 * (`mocks/data/governance/index.ts` → `CROSS_FRANCHISE_VIEW`) grants this exact
 * string to admin only, and the Axis-B resolver (`orgScope.ts`) takes it as the
 * decoupled `hasCrossFranchiseView` boolean this module derives here. Kept as a
 * named const so the two definitions can be unified once the S0.2-owned
 * `routePermissions.EXTENDED_PERMISSIONS` file is free (followUp recorded); the
 * S0.4 parity test asserts it still equals the seed literal (drift guard).
 */
export const CROSS_FRANCHISE_VIEW_PERMISSION = 'crossFranchiseView';

/** The status literal that includes a role in resolution (others are excluded). */
const ACTIVE_STATUS = 'active';

/**
 * The full role shape Axis A reads: the graph edges (`RoleClosureNode`) plus the
 * grant list, the status filter, and the `isSystem` flag. `GovernanceRole` is
 * assignable verbatim. `isSystem` is carried for documentation only — it is
 * INTENTIONALLY not consulted (superadmin power comes from the seeded grants).
 */
export interface RoleNode extends RoleClosureNode {
  permissions: readonly string[];
  /** `'active'` | `'inactive'`; a non-active role contributes no grants. */
  status?: string;
  /** Carried but NEVER read — superadmin is grants, not this flag (no bypass). */
  isSystem?: boolean;
}

/** The resolver's dataset input — identity + the two catalogs it composes over. */
export interface EffectiveAccessInput {
  identity: PrincipalIdentity;
  roles: readonly RoleNode[];
  orgs: readonly OrgScopeNode[];
}

/**
 * Resolver options. `deny` is the reserved XACML deny-override hook — INERT in v1
 * (allow-only union): it is accepted so a future SoD / franchise carve-out is a
 * designed-in parameter, but NO path consults it yet (asserted inert by the S0.4
 * test). See plan §Resolver contract / Appendix A#6.
 */
export interface EffectiveAccessOptions {
  deny?: readonly DenyRule[];
}

/** Dedupe the seed role ids (flat `roleIds` ∪ every `assignments[].roleId`), order-preserving. */
function collectSeedRoleIds(identity: PrincipalIdentity): string[] {
  const seeds = new Set<string>(identity.roleIds ?? []);
  for (const assignment of identity.assignments ?? []) {
    if (assignment.roleId) seeds.add(assignment.roleId);
  }
  return [...seeds];
}

/** The Axis-A result — grants + per-code provenance, shared by resolve + explain. */
interface AxisAGrants {
  grants: Set<string>;
  sources: Map<string, GrantSource[]>;
}

/**
 * Axis A only (WHAT): union of grants over the `inheritsFrom` closure, then
 * implication-expanded, every code source-tagged. Pure and org-free — reused by
 * both `resolveEffectiveAccess` (composes Axis B) and `explainDecision`'s
 * per-role "who would grant this" probe.
 *
 * Provenance rule: a code granted straight on a held role is `direct`; reached
 * through an `inheritsFrom` edge, `inherited-junior`; materialised purely by the
 * implication table, `implication` (recording the code that implied it). An
 * implication source is added ONLY for a phantom code that has no direct/inherited
 * source — a really-granted code keeps its real provenance and is never re-tagged.
 */
function resolveAxisAGrants(
  seedRoleIds: readonly string[],
  roles: readonly RoleNode[]
): AxisAGrants {
  const directSeeds = new Set(seedRoleIds);
  const closure = resolveRoleClosure(seedRoleIds, roles);
  const grants = new Set<string>();
  const sources = new Map<string, GrantSource[]>();
  const addSource = (code: string, source: GrantSource): void => {
    const existing = sources.get(code);
    if (existing) existing.push(source);
    else sources.set(code, [source]);
  };

  // Phase 1 — role-grant union (catalog order ⇒ deterministic provenance).
  for (const role of roles) {
    if (!closure.has(role.id)) continue;
    if (role.status && role.status !== ACTIVE_STATUS) continue; // inactive excluded
    const source: GrantSource = directSeeds.has(role.id)
      ? { kind: 'direct', roleId: role.id }
      : { kind: 'inherited-junior', roleId: role.id };
    for (const code of role.permissions) {
      grants.add(code);
      addSource(code, source);
    }
  }

  // Phase 2 — implication expansion (manage ⇒ view …), provenance-tagged. Only a
  // NEWLY materialised phantom gets an implication source; base codes keep theirs.
  //
  // CANONICAL ORDER (CU-0): each expansion wave processes codes in SORTED
  // order, so which implier a multi-implied phantom names is a function of
  // the granted SET — never of catalog serialization order. The mock world
  // (fixture-ordered arrays) and the live server (relational storage, sorted
  // arrays) must explain the same phantom identically or the L2 shadow diff
  // goes red on `implied:*` content.
  let frontier: string[] = [...grants].sort();
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const code of frontier) {
      for (const implied of directImplications(code)) {
        if (grants.has(implied)) continue; // already granted ⇒ keep its existing source
        grants.add(implied);
        addSource(implied, { kind: 'implication', ofPermission: code });
        next.push(implied);
      }
    }
    frontier = next.sort();
  }

  return { grants, sources };
}

/**
 * The one pure function both consumers derive from — Axis A ∧ the composed Axis B.
 * Computes the implication-expanded grants, then resolves the visible org set,
 * gating the franchise descent-stop on whether Axis A granted `crossFranchiseView`.
 */
export function resolveEffectiveAccess(
  input: EffectiveAccessInput,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- `options.deny` is the reserved, intentionally-inert v1 deny hook
  options: EffectiveAccessOptions = {}
): EffectiveAccess {
  const { identity, roles, orgs } = input;
  const seedRoleIds = collectSeedRoleIds(identity);
  const { grants, sources } = resolveAxisAGrants(seedRoleIds, roles);
  const hasCrossFranchiseView = grants.has(CROSS_FRANCHISE_VIEW_PERMISSION);
  const visibleOrgIds = resolveVisibleOrgIds(identity, orgs, { hasCrossFranchiseView });
  // v1 is allow-only: `options.deny` (XACML deny-override) is reserved and
  // intentionally NOT consulted — scope only narrows, grants never subtract.
  return { grants, visibleOrgIds, sources };
}

/**
 * Explain one access check (Axis A ∧ Axis B) for the WHY-403 explainer (S3.2) and
 * the effective-permission preview (S3.1). `orgId` omitted (or null) ⇒ Axis B is
 * not evaluated (`axisB === true`, shared master data). `grantingRoleIds` is the
 * remediation hint — every active role whose OWN effective grants would include
 * the permission (so "assign role X to gain it" is answerable).
 */
export function explainDecision(
  input: EffectiveAccessInput,
  permission: string,
  orgId?: string | null
): AccessDecision {
  const { identity, roles, orgs } = input;
  const seedRoleIds = collectSeedRoleIds(identity);
  const { grants, sources } = resolveAxisAGrants(seedRoleIds, roles);
  const axisA = grants.has(permission);

  const evaluateAxisB = orgId != null;
  const axisB = evaluateAxisB
    ? isRecordVisible(
        orgId,
        resolveVisibleOrgIds(identity, orgs, {
          hasCrossFranchiseView: grants.has(CROSS_FRANCHISE_VIEW_PERMISSION),
        })
      )
    : true;

  const grantingRoleIds = roles
    .filter((role) => !role.status || role.status === ACTIVE_STATUS)
    .filter((role) => resolveAxisAGrants([role.id], roles).grants.has(permission))
    .map((role) => role.id);

  return {
    permission,
    ...(evaluateAxisB ? { orgId } : {}),
    allowed: axisA && axisB,
    axisA,
    axisB,
    sources: sources.get(permission) ?? [],
    heldRoleIds: seedRoleIds,
    grantingRoleIds,
  };
}
